/**
 * useEpisodes hook for fetching and managing episode list
 * Follows functional programming principles: no classes, no let, immutability
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchEpisodes, createEpisode as apiCreateEpisode, deleteEpisode as apiDeleteEpisode, updateEpisode as apiUpdateEpisode } from '../api/client';

export type EpisodeListItem = Readonly<{
  id: string;
  number?: number;
  title: string;
  date?: string;
  showTitle?: string;
  createdAt: string;
}>;

export type CreateEpisodeData = Readonly<{
  number?: number | null;
  title: string;
  date?: string | null;
  showTitle?: string | null;
}>;

export type UpdateEpisodeData = Readonly<{
  number?: number | null;
  title?: string;
  date?: string | null;
  showTitle?: string | null;
}>;

export type EpisodesState = Readonly<{
  episodes: readonly EpisodeListItem[];
  loading: boolean;
  error: string | null;
}>;

type UseEpisodesReturn = Readonly<{
  episodes: readonly EpisodeListItem[];
  loading: boolean;
  error: string | null;
  createEpisode: (data: CreateEpisodeData) => Promise<void>;
  updateEpisode: (id: string, data: UpdateEpisodeData) => Promise<void>;
  deleteEpisode: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
}>;

/**
 * Sort episodes by date descending (newest first)
 * Episodes with dates come first, then by createdAt
 */
const sortEpisodesByDate = (
  episodes: readonly EpisodeListItem[]
): readonly EpisodeListItem[] => {
  return [...episodes].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : new Date(a.createdAt).getTime();
    const dateB = b.date ? new Date(b.date).getTime() : new Date(b.createdAt).getTime();
    return dateB - dateA;
  });
};

const initialState: EpisodesState = {
  episodes: [],
  loading: true,
  error: null,
};

export const useEpisodes = (): UseEpisodesReturn => {
  const [state, setState] = useState<EpisodesState>(initialState);

  const loadEpisodes = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const episodes = await fetchEpisodes();
      const sortedEpisodes = sortEpisodesByDate(episodes as EpisodeListItem[]);
      
      setState({
        episodes: sortedEpisodes,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load episodes',
      }));
    }
  }, []);

  useEffect(() => {
    loadEpisodes();
  }, [loadEpisodes]);

  const createEpisode = useCallback(async (data: CreateEpisodeData): Promise<void> => {
    try {
      const newEpisode = await apiCreateEpisode(data);
      
      setState((prev) => {
        const updatedEpisodes = [...prev.episodes, newEpisode as EpisodeListItem];
        return {
          ...prev,
          episodes: sortEpisodesByDate(updatedEpisodes),
          error: null,
        };
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to create episode',
      }));
      throw err;
    }
  }, []);

  const updateEpisode = useCallback(async (id: string, data: UpdateEpisodeData): Promise<void> => {
    try {
      const updated = await apiUpdateEpisode(id, data);
      
      setState((prev) => {
        const updatedEpisodes = prev.episodes.map((ep) =>
          ep.id === id ? { ...ep, ...updated } as EpisodeListItem : ep
        );
        return {
          ...prev,
          episodes: sortEpisodesByDate(updatedEpisodes),
          error: null,
        };
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to update episode',
      }));
      throw err;
    }
  }, []);

  const deleteEpisode = useCallback(async (id: string): Promise<void> => {
    try {
      await apiDeleteEpisode(id);
      
      setState((prev) => ({
        ...prev,
        episodes: prev.episodes.filter((ep) => ep.id !== id),
        error: null,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to delete episode',
      }));
      throw err;
    }
  }, []);

  const refetch = useCallback(async (): Promise<void> => {
    await loadEpisodes();
  }, [loadEpisodes]);

  return {
    episodes: state.episodes,
    loading: state.loading,
    error: state.error,
    createEpisode,
    updateEpisode,
    deleteEpisode,
    refetch,
  };
};
